/**
 * Full-screen finisher clip played with a live WebGL chroma/luma key, so the
 * background drops out and the game shows through — on BOTH iOS (WKWebView) and
 * Android (Chromium). Unlike `<video>` alpha (HEVC/VP9), this works everywhere
 * because we decode the clip's RGB frames into a GL texture and key them in a
 * shader; the result composites over the page (the game canvas) via the
 * overlay canvas's own alpha.
 *
 * Input is a plain H.264 MP4 shot on a solid GREEN ('green' → chroma key) or
 * BLACK ('black' → luma key, for glow FX) background. No pre-keying needed.
 */
export type FinisherKeyType = 'green' | 'black';

const VERT = `
attribute vec2 aPos;
varying vec2 vUV;
uniform vec2 uCover;
void main() {
  vec2 uv = (aPos + 1.0) * 0.5;
  vUV = (uv - 0.5) * uCover + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

const FRAG = `
precision mediump float;
varying vec2 vUV;
uniform sampler2D uTex;
uniform int uMode;          // 0 = green chroma, 1 = black luma
uniform float uCrop;        // cut this fraction off the bottom (watermark strip)
void main() {
  if (vUV.x < 0.0 || vUV.x > 1.0 || vUV.y < 0.0 || vUV.y > 1.0 || vUV.y < uCrop) {
    gl_FragColor = vec4(0.0);
    return;
  }
  vec4 c = texture2D(uTex, vUV);
  vec3 rgb = c.rgb;
  float a = 1.0;
  if (uMode == 0) {
    float m = max(rgb.r, rgb.b);
    float d = rgb.g - m;                 // how "green-dominant" the pixel is
    a = 1.0 - smoothstep(0.04, 0.20, d); // soft key
    rgb.g = min(rgb.g, m);               // suppress green spill
  } else {
    float l = max(rgb.r, max(rgb.g, rgb.b)); // brightness
    a = smoothstep(0.04, 0.22, l);
  }
  gl_FragColor = vec4(rgb * a, a);       // premultiplied
}`;

function compile(gl: WebGLRenderingContext, type: number, src: string): WebGLShader | null {
  const sh = gl.createShader(type);
  if (!sh) return null;
  gl.shaderSource(sh, src);
  gl.compileShader(sh);
  if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) {
    console.warn('[finisher] shader compile failed', gl.getShaderInfoLog(sh));
    gl.deleteShader(sh);
    return null;
  }
  return sh;
}

export class VideoFinisher {
  private canvas: HTMLCanvasElement | null = null;
  private video: HTMLVideoElement | null = null;
  private gl: WebGLRenderingContext | null = null;
  private prog: WebGLProgram | null = null;
  private tex: WebGLTexture | null = null;
  private buf: WebGLBuffer | null = null;
  private raf = 0;
  private capTimer = 0;
  private done = false;
  private onDone: (() => void) | null = null;

  /** Play `src` keyed by `keyType` for at most `maxMs`, then call `onDone`
   * (also fired on natural end / error). `cropBottom` (0..0.3) cuts that
   * fraction off the bottom of the clip — e.g. to drop a watermark strip.
   * Safe to call once per instance. */
  play(
    src: string,
    keyType: FinisherKeyType,
    maxMs: number,
    onDone: () => void,
    cropBottom = 0,
  ): void {
    this.onDone = onDone;
    try {
      const canvas = document.createElement('canvas');
      canvas.style.cssText =
        'position:fixed;inset:0;width:100vw;height:100vh;z-index:9999;pointer-events:none;opacity:1;transition:opacity .3s ease;';
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.max(2, Math.round(window.innerWidth * dpr));
      canvas.height = Math.max(2, Math.round(window.innerHeight * dpr));
      document.body.appendChild(canvas);
      this.canvas = canvas;

      const gl = canvas.getContext('webgl', {
        premultipliedAlpha: true,
        alpha: true,
        antialias: false,
      }) as WebGLRenderingContext | null;
      if (!gl) return this.finish();
      this.gl = gl;

      const vs = compile(gl, gl.VERTEX_SHADER, VERT);
      const fs = compile(gl, gl.FRAGMENT_SHADER, FRAG);
      if (!vs || !fs) return this.finish();
      const prog = gl.createProgram()!;
      gl.attachShader(prog, vs);
      gl.attachShader(prog, fs);
      gl.linkProgram(prog);
      if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) {
        console.warn('[finisher] program link failed', gl.getProgramInfoLog(prog));
        return this.finish();
      }
      this.prog = prog;

      // Fullscreen triangle-strip quad.
      const buf = gl.createBuffer();
      gl.bindBuffer(gl.ARRAY_BUFFER, buf);
      gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
      this.buf = buf;

      const tex = gl.createTexture();
      gl.bindTexture(gl.TEXTURE_2D, tex);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
      this.tex = tex;

      gl.enable(gl.BLEND);
      gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA); // premultiplied compositing
      gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);

      const aPos = gl.getAttribLocation(prog, 'aPos');
      const uCover = gl.getUniformLocation(prog, 'uCover');
      const uMode = gl.getUniformLocation(prog, 'uMode');
      const uTex = gl.getUniformLocation(prog, 'uTex');
      const uCrop = gl.getUniformLocation(prog, 'uCrop');
      const mode = keyType === 'black' ? 1 : 0;
      const crop = Math.max(0, Math.min(0.3, cropBottom));

      const video = document.createElement('video');
      // CORS only matters for cross-origin streaming; setting crossOrigin on a
      // blob: URL makes Chromium/Android WebView FAIL the load. Blob is
      // same-origin (no taint), so only set it for http(s) sources.
      if (/^https?:/i.test(src)) video.crossOrigin = 'anonymous';
      video.src = src;
      video.muted = false;
      video.loop = false;
      video.playsInline = true;
      video.setAttribute('playsinline', '');
      video.setAttribute('webkit-playsinline', '');
      video.style.cssText = 'position:fixed;width:2px;height:2px;opacity:0;pointer-events:none;left:-10px;top:-10px;';
      document.body.appendChild(video);
      this.video = video;
      video.addEventListener('ended', () => this.finish(), { once: true });
      video.addEventListener('error', () => this.finish(), { once: true });
      // Try with sound; if the platform blocks audible autoplay, retry muted.
      video.play().catch(() => {
        video.muted = true;
        video.play().catch(() => { /* give up; cap timer ends it */ });
      });

      const render = (): void => {
        if (this.done || !this.gl) return;
        const g = this.gl;
        if (video.readyState >= 2 && video.videoWidth > 0) {
          // cover-fit: zoom so the clip fills the screen, cropping the overflow.
          const ca = canvas.width / canvas.height;
          const va = video.videoWidth / video.videoHeight;
          const sx = va > ca ? ca / va : 1;
          const sy = va > ca ? 1 : va / ca;
          try {
            g.bindTexture(g.TEXTURE_2D, tex);
            g.texImage2D(g.TEXTURE_2D, 0, g.RGBA, g.RGBA, g.UNSIGNED_BYTE, video);
          } catch {
            // cross-origin without CORS → tainted; abort gracefully.
            return this.finish();
          }
          g.viewport(0, 0, canvas.width, canvas.height);
          g.clearColor(0, 0, 0, 0);
          g.clear(g.COLOR_BUFFER_BIT);
          g.useProgram(prog);
          g.bindBuffer(g.ARRAY_BUFFER, buf);
          g.enableVertexAttribArray(aPos);
          g.vertexAttribPointer(aPos, 2, g.FLOAT, false, 0, 0);
          g.uniform2f(uCover, sx, sy);
          g.uniform1i(uMode, mode);
          g.uniform1f(uCrop, crop);
          g.uniform1i(uTex, 0);
          g.activeTexture(g.TEXTURE0);
          g.bindTexture(g.TEXTURE_2D, tex);
          g.drawArrays(g.TRIANGLE_STRIP, 0, 4);
        }
        this.raf = requestAnimationFrame(render);
      };
      this.raf = requestAnimationFrame(render);
      this.capTimer = window.setTimeout(() => this.finish(), Math.max(800, maxMs));
    } catch (e) {
      console.warn('[finisher] video finisher failed', e);
      this.finish();
    }
  }

  /** End playback now (fade out + cleanup + onDone). Idempotent. */
  destroy(): void {
    this.finish();
  }

  private finish(): void {
    if (this.done) return;
    this.done = true;
    if (this.raf) cancelAnimationFrame(this.raf);
    if (this.capTimer) window.clearTimeout(this.capTimer);
    const cb = this.onDone;
    this.onDone = null;
    const canvas = this.canvas;
    // Fade the overlay out, then tear everything down.
    if (canvas) canvas.style.opacity = '0';
    window.setTimeout(() => {
      try { this.video?.pause(); } catch { /* */ }
      this.video?.remove();
      const gl = this.gl;
      if (gl) {
        if (this.tex) gl.deleteTexture(this.tex);
        if (this.buf) gl.deleteBuffer(this.buf);
        if (this.prog) gl.deleteProgram(this.prog);
        const lose = gl.getExtension('WEBGL_lose_context');
        lose?.loseContext();
      }
      canvas?.remove();
      this.canvas = null;
      this.video = null;
      this.gl = null;
    }, 320);
    cb?.();
  }
}
